import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useDatePreferences } from '../hooks/useDatePreferences'
import { useUnsavedChanges, isFormDirty } from '../hooks/useUnsavedChanges'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import QRCode from 'qrcode'
import {
  User,
  Building2,
  Bell,
  Key,
  Save,
  AlertCircle,
  CheckCircle,
  Shield,
  Mail,
  Plus,
  Trash2,
  Edit,
  X,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  Clock,
  Terminal,
  Archive,
  Smartphone,
  Monitor,
  Globe,
  LogOut,
  FileText,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  QrCode,
  KeyRound,
  HardDrive,
  Database,
  FileJson,
  FileSpreadsheet,
  Package,
  CheckSquare,
  MapPin,
  Languages,
  Skull,
} from 'lucide-react'
import { TIMEZONES, LOCALES, getBrowserTimezone, getBrowserLocale } from '../lib/dateUtils'

type ProfileState = {
  first_name: string
  last_name: string
  phone: string
}

type LocalizationState = {
  timezone: string
  locale: string
}

type OrganizationState = {
  company_name: string
  company_description: string
  website: string
  domains: string[]
  industry: string
  employee_count: string
  primary_contact_name: string
  primary_contact_email: string
  primary_contact_phone: string
}

type NotificationsState = {
  email_notifications: boolean
  evidence_due_reminder: boolean
  evidence_overdue_alert: boolean
  risk_threshold_alert: boolean
  assessment_assigned: boolean
  assessment_approved: boolean
  weekly_summary: boolean
}


type ApiKeyType = {
  id: string
  tenant_id: string
  user_id: string
  name: string
  key_prefix: string
  scopes: string[]
  last_used_at: number | null
  use_count: number
  expires_at: number | null
  revoked_at: number | null
  created_at: number
  updated_at: number
  is_expired: boolean
  is_revoked: boolean
}

type AuditLogEntry = {
  id: string
  tenant_id: string
  user_id: string | null
  username: string | null
  user_display_name: string | null
  action: string
  resource_type: string
  resource_id: string
  details: string | null
  changes: string | null
  ip_address: string | null
  user_agent: string | null
  status: string | null
  error_message: string | null
  timestamp: number
}

type AuditLogFilters = {
  resource_type: string
  user_id: string
  action: string
  start_date: string
  end_date: string
  search: string
}

type DarkWebSettingsState = {
  enableDailyScans: boolean
  emailAlertsEnabled: boolean
  emailRecipients: string[]
  webhookAlertsEnabled: boolean
  notificationChannelIds: string[]
  lastScanAt: number | null
}

export default function Settings() {
  const { user, checkAuth } = useAuth()
  const { formatDate, formatDateTime, formatRelativeTime } = useDatePreferences()
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const toast = useToast()
  const confirmDialog = useConfirm()

  // Profile state
  const [profile, setProfile] = useState<ProfileState>({
    first_name: '',
    last_name: '',
    phone: '',
  })

  // Localization state (timezone and locale)
  const [localization, setLocalization] = useState<LocalizationState>({
    timezone: 'UTC',
    locale: 'en-US',
  })

  // Organization state (admin only)
  const [organization, setOrganization] = useState<OrganizationState>({
    company_name: '',
    company_description: '',
    website: '',
    domains: [] as string[],
    industry: '',
    employee_count: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
  })
  const [newOrgDomain, setNewOrgDomain] = useState('')

  // Notification settings
  const [notifications, setNotifications] = useState<NotificationsState>({
    email_notifications: true,
    evidence_due_reminder: true,
    evidence_overdue_alert: true,
    risk_threshold_alert: true,
    assessment_assigned: true,
    assessment_approved: true,
    weekly_summary: false,
  })

  // Auto task generation settings
  const [autoTaskSettings, setAutoTaskSettings] = useState({
    enabled: true,
    daysBeforeDue: 7,
    defaultPriority: 'medium' as 'critical' | 'high' | 'medium' | 'low',
  })

  // Scan notification settings (organization-wide, admin only)
  const [scanNotificationSettings, setScanNotificationSettings] = useState({
    enabled: true,
    severityThreshold: 'medium' as 'critical' | 'high' | 'medium' | 'low' | 'all',
    notifyTriggeringUser: true,
    notifyAdmins: true,
    additionalRecipients: [] as string[],
  })
  const [newScanRecipient, setNewScanRecipient] = useState('')

  // Evidence retention policy state (Issue #182)
  const [retentionPolicy, setRetentionPolicy] = useState({
    enabled: false,
    period_months: 24,
    action: 'archive' as 'archive' | 'delete',
    exempt_controls: [] as string[],
    exempt_evidence_types: [] as string[],
    notify_before_days: 30,
    last_run_at: null as number | null,
  })
  const [retentionStats, setRetentionStats] = useState({
    total_evidence: 0,
    archived_count: 0,
    pending_archival: 0,
  })
  const [retentionLoading, setRetentionLoading] = useState(false)
  const [retentionSaving, setRetentionSaving] = useState(false)


  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKeyType[]>([])
  const [showApiKeyModal, setShowApiKeyModal] = useState(false)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null)
  const [keyCreatedName, setKeyCreatedName] = useState<string>('')
  const [copiedKey, setCopiedKey] = useState(false)
  const [deletingApiKey, setDeletingApiKey] = useState<string | null>(null)
  const [apiKeyForm, setApiKeyForm] = useState({
    name: '',
    scopes: ['read'] as string[],
    expires_in_days: 0, // 0 = never
  })

  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState<{ enabled: boolean; backup_codes_remaining: number } | null>(null)
  const [twoFALoading, setTwoFALoading] = useState(false)
  const [showSetup2FAModal, setShowSetup2FAModal] = useState(false)
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false)
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false)
  const [setup2FAStep, setSetup2FAStep] = useState<'qr' | 'verify' | 'backup'>('qr')
  const [setup2FAData, setSetup2FAData] = useState<{ secret: string; otpauth_url: string; qrCodeDataUrl: string } | null>(null)
  const [twoFACode, setTwoFACode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [twoFAError, setTwoFAError] = useState('')
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false)

  // Email Change state (Issue #106)
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false)
  const [emailChangeStep, setEmailChangeStep] = useState<'enter' | 'verify' | 'success'>('enter')
  const [newEmail, setNewEmail] = useState('')
  const [emailChangeRequestId, setEmailChangeRequestId] = useState<string | null>(null)
  const [emailChangeCode, setEmailChangeCode] = useState('')
  const [emailChangeLoading, setEmailChangeLoading] = useState(false)
  const [emailChangeError, setEmailChangeError] = useState('')

  // Active Sessions state
  const [sessions, setSessions] = useState<Array<{
    sessionId: string
    deviceInfo: string
    createdAt: number
    lastActivityAt: number
    expiresAt: number
    ipAddress: string | null
    userAgent: string | null
    isCurrent: boolean
  }>>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [revokingSession, setRevokingSession] = useState<string | null>(null)
  const [revokingAll, setRevokingAll] = useState(false)

  // Audit Log state (admin only)
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditPagination, setAuditPagination] = useState({
    total: 0,
    limit: 25,
    offset: 0,
    hasMore: false,
  })
  const [auditFilters, setAuditFilters] = useState<AuditLogFilters>({
    resource_type: '',
    user_id: '',
    action: '',
    start_date: '',
    end_date: '',
    search: '',
  })
  const [auditUsers, setAuditUsers] = useState<Array<{ id: string; username: string; email: string }>>([])
  const [showAuditFilters, setShowAuditFilters] = useState(false)
  const [auditExporting, setAuditExporting] = useState(false)

  // Data Export state (Issue #91)
  type ExportSummary = {
    controls: number
    risks: number
    evidence: number
    documents: number
    vendors: number
    tasks: number
  }
  const [exportSummary, setExportSummary] = useState<ExportSummary | null>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv')
  const [exportSelections, setExportSelections] = useState({
    controls: true,
    risks: true,
    evidence: true,
    documents: true,
    vendors: true,
    tasks: true,
  })
  const [exportIncludeRelated, setExportIncludeRelated] = useState(true)
  const [exportInProgress, setExportInProgress] = useState<string | null>(null)

  // Dark Web Settings state (#303)
  const [darkWebSettings, setDarkWebSettings] = useState<DarkWebSettingsState>({
    enableDailyScans: false,
    emailAlertsEnabled: true,
    emailRecipients: [],
    webhookAlertsEnabled: true,
    notificationChannelIds: [],
    lastScanAt: null,
  })
  const [darkWebLoading, setDarkWebLoading] = useState(false)
  const [darkWebSaving, setDarkWebSaving] = useState(false)

  // Track initial values for dirty detection
  const initialProfileRef = useRef<ProfileState | null>(null)
  const initialLocalizationRef = useRef<LocalizationState | null>(null)
  const initialOrgRef = useRef<OrganizationState | null>(null)
  const initialNotificationsRef = useRef<NotificationsState | null>(null)

  // Calculate dirty state for each form
  const hasUnsavedChanges = useMemo(() => {
    if (loading) return false

    const profileDirty = initialProfileRef.current
      ? isFormDirty(profile, initialProfileRef.current)
      : false

    const localizationDirty = initialLocalizationRef.current
      ? isFormDirty(localization, initialLocalizationRef.current)
      : false

    const orgDirty = initialOrgRef.current
      ? isFormDirty(organization, initialOrgRef.current)
      : false

    const notificationsDirty = initialNotificationsRef.current
      ? isFormDirty(notifications, initialNotificationsRef.current)
      : false

    return profileDirty || localizationDirty || orgDirty || notificationsDirty
  }, [loading, profile, localization, organization, notifications])

  // Warn on navigation if there are unsaved changes
  useUnsavedChanges(hasUnsavedChanges)

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'organization', label: 'Organization', icon: Building2, adminOnly: true },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Key },
    { id: 'dark-web', label: 'Dark Web', icon: Skull, adminOnly: true },
    { id: 'audit', label: 'Audit Logs', icon: FileText, adminOnly: true },
    { id: 'export', label: 'Data Export', icon: HardDrive, adminOnly: true },
  ]

  const visibleTabs = tabs.filter(tab => !tab.adminOnly || user?.role === 'admin')

  // Populate profile form from auth context user data
  useEffect(() => {
    if (user) {
      const profileData = {
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
      }
      setProfile(profileData)
      // Update initial ref when user data loads
      if (!initialProfileRef.current) {
        initialProfileRef.current = { ...profileData }
      }

      // Parse preferences for localization settings
      let prefs: any = {}
      if (user.preferences) {
        if (typeof user.preferences === 'string') {
          try {
            prefs = JSON.parse(user.preferences)
          } catch {
            prefs = {}
          }
        } else {
          prefs = user.preferences
        }
      }
      const localizationData = {
        timezone: prefs.timezone || 'UTC',
        locale: prefs.locale || 'en-US',
      }
      setLocalization(localizationData)
      if (!initialLocalizationRef.current) {
        initialLocalizationRef.current = { ...localizationData }
      }
    }
  }, [user])

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    try {
      // Fetch notification settings
      const notifRes = await api.getNotificationSettings()
      if (notifRes.success && notifRes.notification_settings) {
        const notifData = { ...notifications, ...notifRes.notification_settings }
        setNotifications(notifData)
        initialNotificationsRef.current = { ...notifData }
      }

      // Fetch organization (admin only)
      if (user?.role === 'admin') {
        const orgRes = await api.getOrganization()
        if (orgRes.success && orgRes.organization) {
          // Parse domains from JSON string if needed
          let parsedDomains: string[] = []
          if (orgRes.organization.domains) {
            if (typeof orgRes.organization.domains === 'string') {
              try {
                parsedDomains = JSON.parse(orgRes.organization.domains)
              } catch {
                parsedDomains = []
              }
            } else {
              parsedDomains = orgRes.organization.domains
            }
          }
          const orgData = {
            company_name: orgRes.organization.company_name || '',
            company_description: orgRes.organization.company_description || '',
            website: orgRes.organization.website || '',
            domains: parsedDomains,
            industry: orgRes.organization.industry || '',
            employee_count: orgRes.organization.employee_count || '',
            primary_contact_name: orgRes.organization.primary_contact_name || '',
            primary_contact_email: orgRes.organization.primary_contact_email || '',
            primary_contact_phone: orgRes.organization.primary_contact_phone || '',
          }
          setOrganization(orgData)
          initialOrgRef.current = { ...orgData, domains: [...parsedDomains] }

          // Load auto task generation settings from organization settings
          let parsedSettings: any = {}
          if (orgRes.organization.settings) {
            if (typeof orgRes.organization.settings === 'string') {
              try {
                parsedSettings = JSON.parse(orgRes.organization.settings)
              } catch {
                parsedSettings = {}
              }
            } else {
              parsedSettings = orgRes.organization.settings
            }
          }
          const autoTask = parsedSettings.auto_task_generation || {}
          setAutoTaskSettings({
            enabled: autoTask.enabled !== false,
            daysBeforeDue: autoTask.days_before_due || 7,
            defaultPriority: autoTask.default_priority || 'medium',
          })

          // Load scan notification settings from organization settings
          const scanNotif = parsedSettings.scan_notifications || {}
          setScanNotificationSettings({
            enabled: scanNotif.enabled !== false,
            severityThreshold: scanNotif.severity_threshold || 'medium',
            notifyTriggeringUser: scanNotif.notify_triggering_user !== false,
            notifyAdmins: scanNotif.notify_admins !== false,
            additionalRecipients: scanNotif.additional_recipients || [],
          })
        }

        // Fetch retention policy settings (#182)
        try {
          const retentionRes = await api.getRetentionPolicy()
          if (retentionRes.success) {
            setRetentionPolicy(retentionRes.retention_policy)
            setRetentionStats(retentionRes.stats)
          }
        } catch (retErr) {
          console.error('Failed to load retention policy:', retErr)
        }
      }
    } catch (err) {
      console.error('Failed to load settings:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch API keys when security tab is active
  const fetchApiKeys = useCallback(async () => {
    try {
      const res = await api.getApiKeys()
      if (res.success) {
        setApiKeys(res.api_keys)
      }
    } catch (err) {
      console.error('Failed to load API keys:', err)
    }
  }, [])

  // Fetch 2FA status
  const fetch2FAStatus = useCallback(async () => {
    try {
      const res = await api.get2FAStatus()
      if (res.success) {
        setTwoFAStatus({ enabled: res.enabled, backup_codes_remaining: res.backup_codes_remaining })
      }
    } catch (err) {
      console.error('Failed to load 2FA status:', err)
    }
  }, [])

  useEffect(() => {
    if (activeTab === 'security') {
      fetchApiKeys()
      fetchSessions()
      fetch2FAStatus()
    }
  }, [activeTab, fetchApiKeys, fetch2FAStatus])

  // Fetch active sessions
  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true)
    try {
      const res = await api.getSessions()
      if (res.success) {
        setSessions(res.sessions)
      }
    } catch (err) {
      console.error('Failed to load sessions:', err)
    } finally {
      setSessionsLoading(false)
    }
  }, [])

  // Fetch audit logs with current filters and pagination
  const fetchAuditLogs = useCallback(async (resetOffset = false) => {
    if (user?.role !== 'admin') return

    setAuditLoading(true)
    try {
      const offset = resetOffset ? 0 : auditPagination.offset
      const params: Record<string, any> = {
        limit: auditPagination.limit,
        offset,
      }

      // Add filters
      if (auditFilters.resource_type) params.resource_type = auditFilters.resource_type
      if (auditFilters.user_id) params.user_id = auditFilters.user_id
      if (auditFilters.action) params.action = auditFilters.action
      if (auditFilters.search) params.search = auditFilters.search
      if (auditFilters.start_date) {
        params.start_date = new Date(auditFilters.start_date).getTime()
      }
      if (auditFilters.end_date) {
        // Set end_date to end of day
        const endDate = new Date(auditFilters.end_date)
        endDate.setHours(23, 59, 59, 999)
        params.end_date = endDate.getTime()
      }

      const res = await api.getAuditLogs(params)
      if (res.success) {
        setAuditLogs(res.logs)
        setAuditPagination({
          total: res.pagination.total,
          limit: res.pagination.limit,
          offset: res.pagination.offset,
          hasMore: res.pagination.hasMore,
        })
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err)
      toast.error('Failed to load audit logs')
    } finally {
      setAuditLoading(false)
    }
  }, [user?.role, auditFilters, auditPagination.limit, auditPagination.offset, toast])

  // Fetch users for filter dropdown
  const fetchAuditUsers = useCallback(async () => {
    if (user?.role !== 'admin') return
    try {
      const res = await api.getUsers()
      if (res.success) {
        setAuditUsers(res.users.map((u: any) => ({
          id: u.id,
          username: u.username,
          email: u.email,
        })))
      }
    } catch (err) {
      console.error('Failed to load users for audit filter:', err)
    }
  }, [user?.role])

  // Load audit logs when tab is active
  useEffect(() => {
    if (activeTab === 'audit' && user?.role === 'admin') {
      fetchAuditLogs(true)
      fetchAuditUsers()
    }
  }, [activeTab, user?.role, fetchAuditUsers])

  // Re-fetch when filters change
  useEffect(() => {
    if (activeTab === 'audit' && user?.role === 'admin') {
      fetchAuditLogs(true)
    }
  }, [auditFilters])

  // Handle audit log pagination
  const handleAuditPageChange = (newOffset: number) => {
    setAuditPagination(prev => ({ ...prev, offset: newOffset }))
  }

  // Watch for offset changes to trigger fetch
  useEffect(() => {
    if (activeTab === 'audit' && user?.role === 'admin' && auditPagination.offset > 0) {
      fetchAuditLogs(false)
    }
  }, [auditPagination.offset])

  // Export audit logs
  const handleExportAuditLogs = async () => {
    setAuditExporting(true)
    try {
      const params: Record<string, number | undefined> = {}
      if (auditFilters.start_date) {
        params.start_date = new Date(auditFilters.start_date).getTime()
      }
      if (auditFilters.end_date) {
        const endDate = new Date(auditFilters.end_date)
        endDate.setHours(23, 59, 59, 999)
        params.end_date = endDate.getTime()
      }

      const blob = await api.exportAuditLogs(params)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast.success('Audit log exported successfully')
    } catch (err) {
      console.error('Failed to export audit logs:', err)
      toast.error('Failed to export audit logs')
    } finally {
      setAuditExporting(false)
    }
  }

  // Clear all audit filters
  const clearAuditFilters = () => {
    setAuditFilters({
      resource_type: '',
      user_id: '',
      action: '',
      start_date: '',
      end_date: '',
      search: '',
    })
  }

  // Check if any filters are active
  const hasActiveFilters = Object.values(auditFilters).some(v => v !== '')

  // Fetch export summary data when export tab is active
  const fetchExportSummary = useCallback(async () => {
    if (user?.role !== 'admin') return
    setExportLoading(true)
    try {
      const res = await api.getExportSummary()
      if (res.success) {
        setExportSummary(res.summary)
      }
    } catch (err) {
      console.error('Failed to load export summary:', err)
      toast.error('Failed to load export summary')
    } finally {
      setExportLoading(false)
    }
  }, [user?.role, toast])

  // Load export summary when tab becomes active
  useEffect(() => {
    if (activeTab === 'export' && user?.role === 'admin') {
      fetchExportSummary()
    }
  }, [activeTab, user?.role, fetchExportSummary])

  // Dark Web settings fetch (#303)
  const fetchDarkWebSettings = useCallback(async () => {
    if (user?.role !== 'admin') return
    setDarkWebLoading(true)
    try {
      const res = await api.getDarkWebSettings()
      if (res.success && res.settings) {
        setDarkWebSettings({
          enableDailyScans: res.settings.enableDailyScans ?? false,
          emailAlertsEnabled: res.settings.emailAlertsEnabled ?? true,
          emailRecipients: res.settings.emailRecipients ?? [],
          webhookAlertsEnabled: res.settings.webhookAlertsEnabled ?? true,
          notificationChannelIds: res.settings.notificationChannelIds ?? [],
          lastScanAt: res.settings.lastScanAt ?? null,
        })
      }
    } catch (err) {
      console.error('Failed to load Dark Web settings:', err)
      toast.error('Failed to load Dark Web settings')
    } finally {
      setDarkWebLoading(false)
    }
  }, [user?.role, toast])

  // Load Dark Web settings when tab becomes active
  useEffect(() => {
    if (activeTab === 'dark-web' && user?.role === 'admin') {
      fetchDarkWebSettings()
    }
  }, [activeTab, user?.role, fetchDarkWebSettings])

  // Save Dark Web settings
  const handleSaveDarkWebSettings = async () => {
    setDarkWebSaving(true)
    try {
      const res = await api.updateDarkWebSettings({
        enableDailyScans: darkWebSettings.enableDailyScans,
        emailAlertsEnabled: darkWebSettings.emailAlertsEnabled,
        emailRecipients: darkWebSettings.emailRecipients,
        webhookAlertsEnabled: darkWebSettings.webhookAlertsEnabled,
        notificationChannelIds: darkWebSettings.notificationChannelIds,
      })
      if (res.success) {
        toast.success('Dark Web settings saved')
      } else {
        toast.error('Failed to save Dark Web settings')
      }
    } catch (err) {
      console.error('Failed to save Dark Web settings:', err)
      toast.error('Failed to save Dark Web settings')
    } finally {
      setDarkWebSaving(false)
    }
  }

  // Handle individual exports
  const handleExport = async (type: keyof ExportSummary) => {
    setExportInProgress(type)
    try {
      let result: Blob | { success: boolean; export_date: string; count: number; data: any[] }
      switch (type) {
        case 'controls':
          result = await api.exportControls({ format: exportFormat, include_related: exportIncludeRelated })
          break
        case 'risks':
          result = await api.exportRisks({ format: exportFormat, include_related: exportIncludeRelated })
          break
        case 'evidence':
          result = await api.exportEvidence({ format: exportFormat })
          break
        case 'documents':
          result = await api.exportDocuments({ format: exportFormat })
          break
        case 'vendors':
          result = await api.exportVendors({ format: exportFormat, include_related: exportIncludeRelated })
          break
        case 'tasks':
          result = await api.exportTasks({ format: exportFormat })
          break
        default:
          throw new Error('Unknown export type')
      }

      // Handle blob vs JSON response
      let blob: Blob
      if (result instanceof Blob) {
        blob = result
      } else {
        // Convert JSON to blob
        blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' })
      }

      // Download the file
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${type}-export-${new Date().toISOString().split('T')[0]}.${exportFormat}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`)
    } catch (err: any) {
      console.error(`Failed to export ${type}:`, err)
      toast.error(err.message || `Failed to export ${type}`)
    } finally {
      setExportInProgress(null)
    }
  }

  // Handle export of selected data types
  const handleExportSelected = async () => {
    const selectedTypes = Object.entries(exportSelections)
      .filter(([, selected]) => selected)
      .map(([type]) => type as keyof ExportSummary)

    if (selectedTypes.length === 0) {
      toast.error('Please select at least one data type to export')
      return
    }

    for (const type of selectedTypes) {
      await handleExport(type)
    }
  }

  // Handle full backup export
  const handleFullBackup = async () => {
    setExportInProgress('all')
    try {
      const blob = await api.exportAll()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `full-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast.success('Full backup exported successfully')
    } catch (err: any) {
      console.error('Failed to export full backup:', err)
      toast.error(err.message || 'Failed to export full backup')
    } finally {
      setExportInProgress(null)
    }
  }

  // Toggle export selection
  const toggleExportSelection = (type: keyof typeof exportSelections) => {
    setExportSelections(prev => ({
      ...prev,
      [type]: !prev[type],
    }))
  }

  // Check if all selections are checked
  const allSelected = Object.values(exportSelections).every(v => v)
  const noneSelected = Object.values(exportSelections).every(v => !v)

  // Toggle all selections
  const toggleAllExportSelections = () => {
    const newValue = !allSelected
    setExportSelections({
      controls: newValue,
      risks: newValue,
      evidence: newValue,
      documents: newValue,
      vendors: newValue,
      tasks: newValue,
    })
  }

  // Revoke a specific session
  const handleRevokeSession = async (sessionId: string) => {
    const session = sessions.find(s => s.sessionId === sessionId)
    if (session?.isCurrent) {
      const confirmed = await confirmDialog({
        title: 'End Current Session?',
        message: 'This will log you out of your current session. You will need to log in again.',
        confirmText: 'Log Out',
        type: 'danger',
      })
      if (!confirmed) return
    }

    setRevokingSession(sessionId)
    try {
      const res = await api.revokeSession(sessionId)
      if (res.success) {
        if (res.wasCurrentSession) {
          // User revoked their own session - log them out
          await api.logout()
          window.location.href = '/login'
        } else {
          toast.success('Session revoked successfully')
          fetchSessions()
        }
      } else {
        toast.error('Failed to revoke session')
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to revoke session')
    } finally {
      setRevokingSession(null)
    }
  }

  // Revoke all sessions except current
  const handleRevokeAllSessions = async () => {
    const confirmed = await confirmDialog({
      title: 'Log Out All Other Devices?',
      message: 'This will end all your other active sessions. Those devices will need to log in again.',
      confirmText: 'Log Out All',
      type: 'danger',
    })
    if (!confirmed) return

    setRevokingAll(true)
    try {
      const res = await api.revokeAllSessions(true) // true = keep current session
      if (res.success) {
        toast.success(`Logged out from ${res.revokedCount} device(s)`)
        fetchSessions()
      } else {
        toast.error('Failed to revoke sessions')
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to revoke sessions')
    } finally {
      setRevokingAll(false)
    }
  }

  // formatRelativeTime is now provided by useDatePreferences hook

  // API Key handlers
  const handleCreateApiKey = async () => {
    if (!apiKeyForm.name.trim()) {
      showMessage('error', 'Name is required')
      return
    }

    setSaving(true)
    try {
      const res = await api.createApiKey({
        name: apiKeyForm.name.trim(),
        scopes: apiKeyForm.scopes,
        expires_in_days: apiKeyForm.expires_in_days > 0 ? apiKeyForm.expires_in_days : undefined,
      })

      if (res.success) {
        // Store the key for display (only shown once!)
        setNewlyCreatedKey(res.api_key.key)
        setKeyCreatedName(res.api_key.name)
        setCopiedKey(false)
        // Reset form
        setApiKeyForm({ name: '', scopes: ['read'], expires_in_days: 0 })
        setShowApiKeyModal(false)
        // Refresh list
        fetchApiKeys()
      } else {
        showMessage('error', 'Failed to create API key')
      }
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to create API key')
    } finally {
      setSaving(false)
    }
  }

  const handleRevokeApiKey = async (id: string) => {
    const confirmed = await confirmDialog({
      title: 'Revoke API Key',
      message: 'Are you sure you want to revoke this API key? Any applications using this key will no longer be able to access the API.',
      type: 'danger',
      confirmText: 'Revoke',
    })
    if (!confirmed) return

    setDeletingApiKey(id)
    try {
      const res = await api.revokeApiKey(id)
      if (res.success) {
        showMessage('success', 'API key revoked')
        fetchApiKeys()
      } else {
        showMessage('error', 'Failed to revoke API key')
      }
    } catch (err) {
      showMessage('error', 'Failed to revoke API key')
    } finally {
      setDeletingApiKey(null)
    }
  }

  const copyApiKeyToClipboard = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key)
      setCopiedKey(true)
      setTimeout(() => setCopiedKey(false), 2000)
    } catch (err) {
      showMessage('error', 'Failed to copy to clipboard')
    }
  }

  const toggleApiKeyScope = (scope: string) => {
    setApiKeyForm(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }))
  }

  // 2FA handlers
  const handleSetup2FA = async () => {
    setTwoFALoading(true)
    setTwoFAError('')
    setTwoFACode('')
    setSetup2FAStep('qr')

    try {
      const res = await api.setup2FA()
      if (res.success) {
        // Generate QR code data URL
        const qrCodeDataUrl = await QRCode.toDataURL(res.otpauth_url, {
          width: 200,
          margin: 2,
          color: { dark: '#0A2647', light: '#FFFFFF' },
        })
        setSetup2FAData({
          secret: res.secret,
          otpauth_url: res.otpauth_url,
          qrCodeDataUrl,
        })
        setShowSetup2FAModal(true)
      } else {
        showMessage('error', 'Failed to start 2FA setup')
      }
    } catch (err: any) {
      showMessage('error', err.message || 'Failed to start 2FA setup')
    } finally {
      setTwoFALoading(false)
    }
  }

  const handleVerify2FASetup = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      setTwoFAError('Please enter a 6-digit code')
      return
    }

    setTwoFALoading(true)
    setTwoFAError('')

    try {
      const res = await api.verify2FASetup(twoFACode)
      if (res.success) {
        setBackupCodes(res.backup_codes)
        setSetup2FAStep('backup')
        fetch2FAStatus()
      } else {
        setTwoFAError('Invalid code. Please try again.')
      }
    } catch (err: any) {
      setTwoFAError(err.message || 'Verification failed')
    } finally {
      setTwoFALoading(false)
    }
  }

  const handleDisable2FA = async () => {
    if (!twoFACode) {
      setTwoFAError('Please enter a code')
      return
    }

    setTwoFALoading(true)
    setTwoFAError('')

    try {
      const res = await api.disable2FA(twoFACode)
      if (res.success) {
        showMessage('success', '2FA has been disabled')
        setShowDisable2FAModal(false)
        setTwoFACode('')
        fetch2FAStatus()
      } else {
        setTwoFAError('Invalid code. Please try again.')
      }
    } catch (err: any) {
      setTwoFAError(err.message || 'Failed to disable 2FA')
    } finally {
      setTwoFALoading(false)
    }
  }

  const handleRegenerateBackupCodes = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      setTwoFAError('Please enter a 6-digit code from your authenticator app')
      return
    }

    setTwoFALoading(true)
    setTwoFAError('')

    try {
      const res = await api.regenerate2FABackupCodes(twoFACode)
      if (res.success) {
        setBackupCodes(res.backup_codes)
        setTwoFACode('')
        fetch2FAStatus()
      } else {
        setTwoFAError('Invalid code. Please try again.')
      }
    } catch (err: any) {
      setTwoFAError(err.message || 'Failed to generate backup codes')
    } finally {
      setTwoFALoading(false)
    }
  }

  const copyBackupCodesToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'))
      setCopiedBackupCodes(true)
      setTimeout(() => setCopiedBackupCodes(false), 2000)
    } catch (err) {
      showMessage('error', 'Failed to copy to clipboard')
    }
  }

  const close2FAModals = () => {
    setShowSetup2FAModal(false)
    setShowDisable2FAModal(false)
    setShowBackupCodesModal(false)
    setTwoFACode('')
    setTwoFAError('')
    setBackupCodes([])
    setSetup2FAStep('qr')
    setSetup2FAData(null)
  }

  // Email Change handlers (Issue #106)
  const openEmailChangeModal = () => {
    setShowEmailChangeModal(true)
    setEmailChangeStep('enter')
    setNewEmail('')
    setEmailChangeRequestId(null)
    setEmailChangeCode('')
    setEmailChangeError('')
  }

  const closeEmailChangeModal = () => {
    setShowEmailChangeModal(false)
    setEmailChangeStep('enter')
    setNewEmail('')
    setEmailChangeRequestId(null)
    setEmailChangeCode('')
    setEmailChangeError('')
  }

  const handleRequestEmailChange = async () => {
    if (!newEmail.trim()) {
      setEmailChangeError('Please enter a new email address')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newEmail.trim())) {
      setEmailChangeError('Please enter a valid email address')
      return
    }

    setEmailChangeLoading(true)
    setEmailChangeError('')

    try {
      const res = await api.requestEmailChange(newEmail.trim())
      if (res.success) {
        setEmailChangeRequestId(res.request_id)
        setEmailChangeStep('verify')
        toast.success('Verification code sent to your new email address')
      } else {
        setEmailChangeError('Failed to send verification code')
      }
    } catch (err: any) {
      setEmailChangeError(err.message || 'Failed to request email change')
    } finally {
      setEmailChangeLoading(false)
    }
  }

  const handleVerifyEmailChange = async () => {
    if (!emailChangeCode.trim() || emailChangeCode.length !== 6) {
      setEmailChangeError('Please enter the 6-digit verification code')
      return
    }

    if (!emailChangeRequestId) {
      setEmailChangeError('Invalid request. Please start over.')
      return
    }

    setEmailChangeLoading(true)
    setEmailChangeError('')

    try {
      const res = await api.verifyEmailChange(emailChangeRequestId, emailChangeCode.trim())
      if (res.success) {
        setEmailChangeStep('success')
        toast.success('Email address updated successfully')
        // Refresh user data to reflect new email
        await checkAuth()
      } else {
        setEmailChangeError('Invalid verification code')
      }
    } catch (err: any) {
      if (err.remaining_attempts !== undefined) {
        setEmailChangeError(`Invalid code. ${err.remaining_attempts} attempt(s) remaining.`)
      } else {
        setEmailChangeError(err.message || 'Failed to verify email change')
      }
    } finally {
      setEmailChangeLoading(false)
    }
  }

  // formatDate is now provided by useDatePreferences hook

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      // Save profile data
      const profileRes = await api.updateProfile(profile)
      if (!profileRes.success) {
        showMessage('error', 'Failed to update profile')
        return
      }

      // Save localization preferences
      const prefsRes = await api.updatePreferences({
        timezone: localization.timezone,
        locale: localization.locale,
      })
      if (!prefsRes.success) {
        showMessage('error', 'Failed to update localization preferences')
        return
      }

      showMessage('success', 'Profile and preferences updated successfully')
      // Update initial values to reset dirty state
      initialProfileRef.current = { ...profile }
      initialLocalizationRef.current = { ...localization }
      // Refresh auth context to update user data across the app
      await checkAuth()
    } catch (err) {
      showMessage('error', 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveOrganization = async () => {
    setSaving(true)
    try {
      // Include auto task generation and scan notification settings in the settings JSON
      const dataToSave = {
        ...organization,
        settings: {
          auto_task_generation: {
            enabled: autoTaskSettings.enabled,
            days_before_due: autoTaskSettings.daysBeforeDue,
            default_priority: autoTaskSettings.defaultPriority,
          },
          scan_notifications: {
            enabled: scanNotificationSettings.enabled,
            severity_threshold: scanNotificationSettings.severityThreshold,
            notify_triggering_user: scanNotificationSettings.notifyTriggeringUser,
            notify_admins: scanNotificationSettings.notifyAdmins,
            additional_recipients: scanNotificationSettings.additionalRecipients,
          },
        },
      }
      const res = await api.updateOrganization(dataToSave)
      if (res.success) {
        showMessage('success', 'Organization settings updated')
        // Update initial values to reset dirty state
        initialOrgRef.current = { ...organization, domains: [...organization.domains] }
      }
    } catch (err) {
      showMessage('error', 'Failed to update organization settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveNotifications = async () => {
    setSaving(true)
    try {
      const res = await api.updateNotificationSettings(notifications)
      if (res.success) {
        showMessage('success', 'Notification settings updated')
        // Update initial values to reset dirty state
        initialNotificationsRef.current = { ...notifications }
      }
    } catch (err) {
      showMessage('error', 'Failed to update notification settings')
    } finally {
      setSaving(false)
    }
  }

  // Issue #182: Save retention policy
  const handleSaveRetentionPolicy = async () => {
    setRetentionSaving(true)
    try {
      const res = await api.updateRetentionPolicy({
        enabled: retentionPolicy.enabled,
        period_months: retentionPolicy.period_months,
        action: retentionPolicy.action,
        exempt_controls: retentionPolicy.exempt_controls,
        exempt_evidence_types: retentionPolicy.exempt_evidence_types,
        notify_before_days: retentionPolicy.notify_before_days,
      })
      if (res.success) {
        toast.success('Retention policy updated')
        // Refresh stats
        const statsRes = await api.getRetentionPolicy()
        if (statsRes.success) {
          setRetentionStats(statsRes.stats)
        }
      }
    } catch (err) {
      toast.error('Failed to update retention policy')
    } finally {
      setRetentionSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your account and preferences</p>
      </div>

      {/* Message Toast */}
      {message && (
        <div
          className={`flex items-center gap-2 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          {message.text}
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-48 flex-shrink-0">
          <nav className="space-y-1">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && (
            <div className="card">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
                <p className="text-sm text-gray-500 mt-1">Update your personal information</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">First Name</label>
                    <input
                      type="text"
                      value={profile.first_name}
                      onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                      className="input"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="label">Last Name</label>
                    <input
                      type="text"
                      value={profile.last_name}
                      onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                      className="input"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="label">Email</label>
                  <div className="flex items-center gap-3 max-w-md">
                    <input
                      type="email"
                      value={user?.email || ''}
                      className="input flex-1 bg-gray-50 text-gray-500"
                      disabled
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={openEmailChangeModal}
                      className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                    >
                      <Mail className="h-4 w-4" />
                      Change
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Change your email address (requires verification)
                  </p>
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    className="input max-w-md"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="label">Role</label>
                  <input
                    type="text"
                    value={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}
                    className="input max-w-md bg-gray-50 text-gray-500"
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Contact an administrator to change your role</p>
                </div>

                {/* Localization Settings */}
                <div className="border-t pt-6 mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-900">Localization Settings</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Configure your timezone and locale preferences for date and time display across the platform.
                  </p>
                  <div className="grid grid-cols-2 gap-4 max-w-lg">
                    <div>
                      <label className="label flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        Timezone
                      </label>
                      <select
                        value={localization.timezone}
                        onChange={(e) => setLocalization({ ...localization, timezone: e.target.value })}
                        className="input"
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label} ({tz.offset})
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500">All dates will be shown in this timezone</p>
                        <button
                          type="button"
                          onClick={() => setLocalization({ ...localization, timezone: getBrowserTimezone() })}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          Detect
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="label flex items-center gap-2">
                        <Languages className="h-4 w-4 text-gray-400" />
                        Date Format (Locale)
                      </label>
                      <select
                        value={localization.locale}
                        onChange={(e) => setLocalization({ ...localization, locale: e.target.value })}
                        className="input"
                      >
                        {LOCALES.map((loc) => (
                          <option key={loc.value} value={loc.value}>
                            {loc.label} ({loc.dateFormat})
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500">Date format: {LOCALES.find(l => l.value === localization.locale)?.dateFormat || 'MM/DD/YYYY'}</p>
                        <button
                          type="button"
                          onClick={() => setLocalization({ ...localization, locale: getBrowserLocale() })}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          Detect
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Preview */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg max-w-lg">
                    <p className="text-xs text-gray-500 mb-1">Preview:</p>
                    <p className="text-sm text-gray-900">
                      Current date/time: {new Date().toLocaleString(localization.locale, { timeZone: localization.timezone, dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <button onClick={handleSaveProfile} disabled={saving} className="btn-primary">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'organization' && user?.role === 'admin' && (
            <div className="card">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Organization Settings</h2>
                <p className="text-sm text-gray-500 mt-1">Manage your organization details</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="label">Company Name</label>
                  <input
                    type="text"
                    value={organization.company_name}
                    onChange={(e) => setOrganization({ ...organization, company_name: e.target.value })}
                    className="input max-w-md"
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={organization.company_description}
                    onChange={(e) => setOrganization({ ...organization, company_description: e.target.value })}
                    className="input max-w-md"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="label">Primary Website</label>
                  <input
                    type="url"
                    value={organization.website}
                    onChange={(e) => setOrganization({ ...organization, website: e.target.value })}
                    className="input max-w-md"
                    placeholder="https://example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for TPRM self-assessments</p>
                </div>
                <div>
                  <label className="label">Additional Domains</label>
                  <p className="text-xs text-gray-500 mb-2">Add other domains your organization owns (included in self-assessments)</p>
                  <div className="flex gap-2 mb-2 max-w-md">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="subdomain.example.com"
                      value={newOrgDomain}
                      onChange={(e) => setNewOrgDomain(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          const domain = newOrgDomain.trim()
                          if (domain && !organization.domains.includes(domain)) {
                            setOrganization({ ...organization, domains: [...organization.domains, domain] })
                            setNewOrgDomain('')
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const domain = newOrgDomain.trim()
                        if (domain && !organization.domains.includes(domain)) {
                          setOrganization({ ...organization, domains: [...organization.domains, domain] })
                          setNewOrgDomain('')
                        }
                      }}
                      className="btn-secondary px-4"
                    >
                      Add
                    </button>
                  </div>
                  {organization.domains.length > 0 && (
                    <div className="flex flex-wrap gap-2 max-w-md">
                      {organization.domains.map((domain) => (
                        <span
                          key={domain}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                        >
                          {domain}
                          <button
                            type="button"
                            onClick={() => setOrganization({ ...organization, domains: organization.domains.filter(d => d !== domain) })}
                            className="text-gray-400 hover:text-red-500"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div>
                    <label className="label">Industry</label>
                    <select
                      value={organization.industry}
                      onChange={(e) => setOrganization({ ...organization, industry: e.target.value })}
                      className="input"
                    >
                      <option value="">Select...</option>
                      <option value="technology">Technology</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="finance">Finance</option>
                      <option value="retail">Retail</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Employee Count</label>
                    <select
                      value={organization.employee_count}
                      onChange={(e) => setOrganization({ ...organization, employee_count: e.target.value })}
                      className="input"
                    >
                      <option value="">Select...</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="501-1000">501-1000</option>
                      <option value="1000+">1000+</option>
                    </select>
                  </div>
                </div>
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Primary Contact</h3>
                  <div className="space-y-3 max-w-md">
                    <input
                      type="text"
                      value={organization.primary_contact_name}
                      onChange={(e) => setOrganization({ ...organization, primary_contact_name: e.target.value })}
                      className="input"
                      placeholder="Contact Name"
                    />
                    <input
                      type="email"
                      value={organization.primary_contact_email}
                      onChange={(e) => setOrganization({ ...organization, primary_contact_email: e.target.value })}
                      className="input"
                      placeholder="Contact Email"
                    />
                    <input
                      type="tel"
                      value={organization.primary_contact_phone}
                      onChange={(e) => setOrganization({ ...organization, primary_contact_phone: e.target.value })}
                      className="input"
                      placeholder="Contact Phone"
                    />
                  </div>
                </div>
                {/* Automation Settings */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Automation Settings</h3>
                  <div className="space-y-4 max-w-md">
                    <div className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Auto Task Generation</p>
                        <p className="text-xs text-gray-500">Automatically create tasks when controls are due for review</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={autoTaskSettings.enabled}
                        onChange={(e) => setAutoTaskSettings({ ...autoTaskSettings, enabled: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </div>
                    {autoTaskSettings.enabled && (
                      <>
                        <div>
                          <label className="label">Days Before Due</label>
                          <select
                            value={autoTaskSettings.daysBeforeDue}
                            onChange={(e) => setAutoTaskSettings({ ...autoTaskSettings, daysBeforeDue: parseInt(e.target.value) })}
                            className="input"
                          >
                            <option value={1}>1 day before</option>
                            <option value={3}>3 days before</option>
                            <option value={7}>7 days before</option>
                            <option value={14}>14 days before</option>
                            <option value={30}>30 days before</option>
                            <option value={45}>45 days before</option>
                            <option value={60}>60 days before</option>
                            <option value={90}>90 days before</option>
                            <option value={120}>120 days before</option>
                            <option value={180}>180 days before</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">How early to create tasks before control review is due</p>
                        </div>
                        <div>
                          <label className="label">Default Task Priority</label>
                          <select
                            value={autoTaskSettings.defaultPriority}
                            onChange={(e) => setAutoTaskSettings({ ...autoTaskSettings, defaultPriority: e.target.value as any })}
                            className="input"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">Priority will be elevated automatically as due date approaches</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="pt-4">
                  <button onClick={handleSaveOrganization} disabled={saving} className="btn-primary">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Issue #182: Evidence Retention Policy */}
          {activeTab === 'organization' && user?.role === 'admin' && (
            <div className="card mt-6">
              <div className="p-6 border-b">
                <div className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-gray-400" />
                  <h2 className="text-lg font-semibold text-gray-900">Evidence Retention Policy</h2>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Configure automatic archival or deletion of old evidence based on retention rules
                </p>
              </div>
              <div className="p-6 space-y-4">
                {/* Stats summary */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-semibold text-gray-900">{retentionStats.total_evidence}</p>
                    <p className="text-xs text-gray-500">Total Evidence</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-semibold text-gray-900">{retentionStats.archived_count}</p>
                    <p className="text-xs text-gray-500">Archived</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-semibold text-amber-600">{retentionStats.pending_archival}</p>
                    <p className="text-xs text-gray-500">Pending Archival</p>
                  </div>
                </div>

                {/* Enable/Disable toggle */}
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enable Retention Policy</p>
                    <p className="text-xs text-gray-500">
                      Automatically {retentionPolicy.action} evidence older than the retention period
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={retentionPolicy.enabled}
                    onChange={(e) => setRetentionPolicy({ ...retentionPolicy, enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>

                {retentionPolicy.enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-4 max-w-lg">
                      <div>
                        <label className="label">Retention Period</label>
                        <select
                          value={retentionPolicy.period_months}
                          onChange={(e) => setRetentionPolicy({ ...retentionPolicy, period_months: parseInt(e.target.value) })}
                          className="input"
                        >
                          <option value={3}>3 months</option>
                          <option value={6}>6 months</option>
                          <option value={12}>1 year</option>
                          <option value={24}>2 years</option>
                          <option value={36}>3 years</option>
                          <option value={60}>5 years</option>
                          <option value={84}>7 years</option>
                          <option value={120}>10 years</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Evidence older than this will be processed</p>
                      </div>
                      <div>
                        <label className="label">Action</label>
                        <select
                          value={retentionPolicy.action}
                          onChange={(e) => setRetentionPolicy({ ...retentionPolicy, action: e.target.value as 'archive' | 'delete' })}
                          className="input"
                        >
                          <option value="archive">Archive (can be restored)</option>
                          <option value="delete">Delete permanently</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {retentionPolicy.action === 'archive'
                            ? 'Archived evidence can be restored by admins'
                            : 'Deleted evidence cannot be recovered'}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="label">Notification Window</label>
                      <select
                        value={retentionPolicy.notify_before_days}
                        onChange={(e) => setRetentionPolicy({ ...retentionPolicy, notify_before_days: parseInt(e.target.value) })}
                        className="input max-w-xs"
                      >
                        <option value={0}>No notification</option>
                        <option value={7}>7 days before</option>
                        <option value={14}>14 days before</option>
                        <option value={30}>30 days before</option>
                        <option value={60}>60 days before</option>
                        <option value={90}>90 days before</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Alert admins before evidence is processed</p>
                    </div>

                    {retentionPolicy.action === 'delete' && (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-800">Warning: Permanent Deletion</p>
                          <p className="text-xs text-red-700 mt-1">
                            Evidence that exceeds the retention period will be permanently deleted and cannot be recovered.
                            Consider using "Archive" action instead if you may need to access old evidence.
                          </p>
                        </div>
                      </div>
                    )}

                    {retentionPolicy.last_run_at && (
                      <p className="text-xs text-gray-500">
                        Last run: {new Date(retentionPolicy.last_run_at).toLocaleString()}
                      </p>
                    )}
                  </>
                )}

                <div className="pt-4">
                  <button onClick={handleSaveRetentionPolicy} disabled={retentionSaving} className="btn-primary">
                    <Save className="h-4 w-4 mr-2" />
                    {retentionSaving ? 'Saving...' : 'Save Retention Policy'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <>
            <div className="card">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Notification Settings</h2>
                <p className="text-sm text-gray-500 mt-1">Configure how you receive notifications</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive notifications via email</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.email_notifications}
                    onChange={(e) => setNotifications({ ...notifications, email_notifications: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Evidence Due Reminders</p>
                    <p className="text-sm text-gray-500">Get notified when evidence is due soon</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.evidence_due_reminder}
                    onChange={(e) => setNotifications({ ...notifications, evidence_due_reminder: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Overdue Evidence Alerts</p>
                    <p className="text-sm text-gray-500">Get notified when evidence becomes overdue</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.evidence_overdue_alert}
                    onChange={(e) => setNotifications({ ...notifications, evidence_overdue_alert: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Risk Threshold Alerts</p>
                    <p className="text-sm text-gray-500">Get notified for high-risk items needing attention</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.risk_threshold_alert}
                    onChange={(e) => setNotifications({ ...notifications, risk_threshold_alert: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Assessment Notifications</p>
                    <p className="text-sm text-gray-500">Get notified when assessments are assigned to you</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.assessment_assigned}
                    onChange={(e) => setNotifications({ ...notifications, assessment_assigned: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Weekly Summary</p>
                    <p className="text-sm text-gray-500">Receive a weekly compliance summary email</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notifications.weekly_summary}
                    onChange={(e) => setNotifications({ ...notifications, weekly_summary: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </div>
                <div className="pt-4">
                  <button onClick={handleSaveNotifications} disabled={saving} className="btn-primary">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>

            {/* Scan Notification Settings - Admin Only */}
            {user?.role === 'admin' && (
              <div className="card mt-6">
                <div className="p-6 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">Scan Completion Notifications</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Configure email notifications for vulnerability scan results (organization-wide setting)
                  </p>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between py-3 border-b">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Enable Scan Notifications</p>
                      <p className="text-sm text-gray-500">Send email notifications when scans complete</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={scanNotificationSettings.enabled}
                      onChange={(e) => setScanNotificationSettings({ ...scanNotificationSettings, enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </div>

                  {scanNotificationSettings.enabled && (
                    <>
                      <div className="py-3 border-b">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Severity Threshold
                        </label>
                        <select
                          value={scanNotificationSettings.severityThreshold}
                          onChange={(e) => setScanNotificationSettings({
                            ...scanNotificationSettings,
                            severityThreshold: e.target.value as any
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="all">All findings (always notify)</option>
                          <option value="low">Low and above</option>
                          <option value="medium">Medium and above</option>
                          <option value="high">High and Critical only</option>
                          <option value="critical">Critical only</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Only send notifications when scan finds vulnerabilities at or above this severity
                        </p>
                      </div>

                      <div className="flex items-center justify-between py-3 border-b">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Notify Triggering User</p>
                          <p className="text-sm text-gray-500">Send email to the user who started the scan</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={scanNotificationSettings.notifyTriggeringUser}
                          onChange={(e) => setScanNotificationSettings({
                            ...scanNotificationSettings,
                            notifyTriggeringUser: e.target.checked
                          })}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </div>

                      <div className="flex items-center justify-between py-3 border-b">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Notify All Admins</p>
                          <p className="text-sm text-gray-500">Send email to all admin users for critical/high findings</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={scanNotificationSettings.notifyAdmins}
                          onChange={(e) => setScanNotificationSettings({
                            ...scanNotificationSettings,
                            notifyAdmins: e.target.checked
                          })}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </div>

                      <div className="py-3">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Additional Recipients
                        </label>
                        <p className="text-sm text-gray-500 mb-3">
                          Additional email addresses to notify for all scan completions
                        </p>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="email"
                            value={newScanRecipient}
                            onChange={(e) => setNewScanRecipient(e.target.value)}
                            placeholder="email@example.com"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newScanRecipient && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newScanRecipient)) {
                                if (!scanNotificationSettings.additionalRecipients.includes(newScanRecipient)) {
                                  setScanNotificationSettings({
                                    ...scanNotificationSettings,
                                    additionalRecipients: [...scanNotificationSettings.additionalRecipients, newScanRecipient]
                                  })
                                }
                                setNewScanRecipient('')
                              }
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                          >
                            Add
                          </button>
                        </div>
                        {scanNotificationSettings.additionalRecipients.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {scanNotificationSettings.additionalRecipients.map((email) => (
                              <span
                                key={email}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded"
                              >
                                {email}
                                <button
                                  type="button"
                                  onClick={() => setScanNotificationSettings({
                                    ...scanNotificationSettings,
                                    additionalRecipients: scanNotificationSettings.additionalRecipients.filter(e => e !== email)
                                  })}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="pt-4">
                    <button onClick={handleSaveOrganization} disabled={saving} className="btn-primary">
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Scan Notification Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            </>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Authentication Section */}
              <div className="card">
                <div className="p-6 border-b">
                  <h2 className="text-lg font-semibold text-gray-900">Authentication</h2>
                  <p className="text-sm text-gray-500 mt-1">Your account uses passwordless email verification</p>
                </div>
                <div className="p-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Shield className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-medium text-green-800">Passwordless Authentication Enabled</h3>
                        <p className="text-sm text-green-700 mt-1">
                          Your account is protected with 2-step email verification. No password needed.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-900">How it works:</h3>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-primary-700">1</span>
                        </div>
                        <p className="text-sm text-gray-600">Enter your email address on the login page</p>
                      </div>
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-primary-700">2</span>
                        </div>
                        <p className="text-sm text-gray-600">Check your email for a 6-digit verification code</p>
                      </div>
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-primary-700">3</span>
                        </div>
                        <p className="text-sm text-gray-600">Enter the code to securely access your account</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t">
                    <div className="flex items-center text-sm text-gray-500">
                      <Mail className="h-4 w-4 mr-2" />
                      Verification codes are sent to: <span className="font-medium text-gray-700 ml-1">{user?.email}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Two-Factor Authentication Section */}
              <div className="card">
                <div className="p-6 border-b">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    Two-Factor Authentication (2FA)
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Add an extra layer of security using an authenticator app
                  </p>
                </div>
                <div className="p-6">
                  {twoFAStatus === null ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                  ) : twoFAStatus.enabled ? (
                    <div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <h3 className="text-sm font-medium text-green-800">2FA is Enabled</h3>
                            <p className="text-sm text-green-700 mt-1">
                              Your account is protected with two-factor authentication.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <KeyRound className="h-5 w-5 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Backup Codes</p>
                              <p className="text-xs text-gray-500">
                                {twoFAStatus.backup_codes_remaining} of 10 codes remaining
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setTwoFACode('')
                              setTwoFAError('')
                              setBackupCodes([])
                              setShowBackupCodesModal(true)
                            }}
                            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Generate New Codes
                          </button>
                        </div>

                        <button
                          onClick={() => {
                            setTwoFACode('')
                            setTwoFAError('')
                            setShowDisable2FAModal(true)
                          }}
                          className="btn-danger w-full"
                        >
                          Disable 2FA
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <h3 className="text-sm font-medium text-amber-800">2FA is Not Enabled</h3>
                            <p className="text-sm text-amber-700 mt-1">
                              Enable two-factor authentication for additional security.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Two-factor authentication adds an extra layer of security to your account.
                          When enabled, you will need to enter a code from your authenticator app in
                          addition to your email verification.
                        </p>

                        <div className="text-sm text-gray-600">
                          <p className="font-medium mb-2">Supported authenticator apps:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-500">
                            <li>Google Authenticator</li>
                            <li>Microsoft Authenticator</li>
                            <li>Authy</li>
                            <li>1Password</li>
                          </ul>
                        </div>

                        <button
                          onClick={handleSetup2FA}
                          disabled={twoFALoading}
                          className="btn-primary w-full"
                        >
                          {twoFALoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Setting up...
                            </>
                          ) : (
                            <>
                              <QrCode className="h-4 w-4 mr-2" />
                              Enable 2FA
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* API Keys Section */}
              <div className="card">
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Terminal className="h-5 w-5" />
                      API Keys
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Create API keys for programmatic access to the API
                    </p>
                  </div>
                  <button
                    onClick={() => setShowApiKeyModal(true)}
                    className="btn-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Key
                  </button>
                </div>

                {/* Newly Created Key Alert */}
                {newlyCreatedKey && (
                  <div className="p-4 bg-amber-50 border-b border-amber-200">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-amber-800">
                          API Key Created: {keyCreatedName}
                        </h3>
                        <p className="text-sm text-amber-700 mt-1 mb-3">
                          Copy your API key now. You will not be able to see it again!
                        </p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded text-sm font-mono text-gray-900">
                            {newlyCreatedKey}
                          </code>
                          <button
                            onClick={() => copyApiKeyToClipboard(newlyCreatedKey)}
                            className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                              copiedKey
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            }`}
                          >
                            {copiedKey ? (
                              <>
                                <Check className="h-4 w-4" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="h-4 w-4" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                        <button
                          onClick={() => setNewlyCreatedKey(null)}
                          className="text-xs text-amber-600 hover:text-amber-700 mt-2"
                        >
                          Dismiss (I have copied the key)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="divide-y">
                  {apiKeys.filter(k => !k.is_revoked).length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Key className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No API keys created</p>
                      <p className="text-xs mt-1">
                        Create an API key to access the API programmatically
                      </p>
                    </div>
                  ) : (
                    apiKeys.filter(k => !k.is_revoked).map((key) => (
                      <div key={key.id} className="p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{key.name}</span>
                            <code className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">
                              {key.key_prefix}...
                            </code>
                            {key.is_expired && (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                Expired
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              Scopes: {key.scopes.join(', ')}
                            </span>
                            {key.last_used_at && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Last used: {new Date(key.last_used_at).toLocaleDateString()}
                              </span>
                            )}
                            <span>
                              Used {key.use_count} time{key.use_count !== 1 ? 's' : ''}
                            </span>
                            {key.expires_at && (
                              <span>
                                Expires: {new Date(key.expires_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeApiKey(key.id)}
                          disabled={deletingApiKey === key.id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                          title="Revoke API key"
                        >
                          {deletingApiKey === key.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Show revoked keys in a separate section */}
                {apiKeys.filter(k => k.is_revoked).length > 0 && (
                  <div className="border-t">
                    <details className="group">
                      <summary className="px-4 py-3 cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2">
                        <span>Show revoked keys ({apiKeys.filter(k => k.is_revoked).length})</span>
                      </summary>
                      <div className="divide-y border-t bg-gray-50">
                        {apiKeys.filter(k => k.is_revoked).map((key) => (
                          <div key={key.id} className="p-4 flex items-center justify-between opacity-60">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-500 line-through">{key.name}</span>
                                <code className="text-xs px-2 py-0.5 bg-gray-200 rounded text-gray-500 font-mono">
                                  {key.key_prefix}...
                                </code>
                                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                                  Revoked
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-400">
                                Revoked: {key.revoked_at ? new Date(key.revoked_at).toLocaleString() : 'Unknown'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>

              {/* Create API Key Modal */}
              {showApiKeyModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div
                      className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                      onClick={() => setShowApiKeyModal(false)}
                    />
                    <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                      <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Create API Key</h3>
                          <button
                            onClick={() => setShowApiKeyModal(false)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="label">Name *</label>
                            <input
                              type="text"
                              value={apiKeyForm.name}
                              onChange={(e) => setApiKeyForm({ ...apiKeyForm, name: e.target.value })}
                              className="input"
                              placeholder="e.g., CI/CD Pipeline, Integration Script"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              A descriptive name to identify this key
                            </p>
                          </div>

                          <div>
                            <label className="label">Scopes *</label>
                            <p className="text-xs text-gray-500 mb-2">
                              Select what this key can do
                            </p>
                            <div className="space-y-2">
                              <label className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={apiKeyForm.scopes.includes('read')}
                                  onChange={() => toggleApiKeyScope('read')}
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-900">Read</span>
                                  <p className="text-xs text-gray-500">
                                    View data (controls, evidence, risks, vendors, etc.)
                                  </p>
                                </div>
                              </label>
                              <label className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={apiKeyForm.scopes.includes('write')}
                                  onChange={() => toggleApiKeyScope('write')}
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
                                />
                                <div>
                                  <span className="text-sm font-medium text-gray-900">Write</span>
                                  <p className="text-xs text-gray-500">
                                    Create and update data (includes read access)
                                  </p>
                                </div>
                              </label>
                              {user?.role === 'admin' && (
                                <label className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                  <input
                                    type="checkbox"
                                    checked={apiKeyForm.scopes.includes('admin')}
                                    onChange={() => toggleApiKeyScope('admin')}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">Admin</span>
                                    <p className="text-xs text-gray-500">
                                      Full access including user management and settings
                                    </p>
                                  </div>
                                </label>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="label">Expiration</label>
                            <select
                              value={apiKeyForm.expires_in_days}
                              onChange={(e) => setApiKeyForm({ ...apiKeyForm, expires_in_days: parseInt(e.target.value) })}
                              className="input"
                            >
                              <option value={0}>Never expires</option>
                              <option value={7}>7 days</option>
                              <option value={30}>30 days</option>
                              <option value={90}>90 days</option>
                              <option value={180}>180 days</option>
                              <option value={365}>1 year</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                        <button
                          onClick={handleCreateApiKey}
                          disabled={saving || apiKeyForm.scopes.length === 0}
                          className="btn-primary w-full sm:ml-3 sm:w-auto"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Key className="h-4 w-4 mr-2" />
                              Create API Key
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setShowApiKeyModal(false)}
                          className="btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Sessions Section */}
              <div className="card">
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Monitor className="h-5 w-5" />
                      Active Sessions
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Devices and browsers currently logged into your account
                    </p>
                  </div>
                  {sessions.length > 1 && (
                    <button
                      onClick={handleRevokeAllSessions}
                      disabled={revokingAll}
                      className="btn-secondary text-red-600 border-red-200 hover:bg-red-50"
                    >
                      {revokingAll ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Revoking...
                        </>
                      ) : (
                        <>
                          <LogOut className="h-4 w-4 mr-2" />
                          Log Out All Devices
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="divide-y">
                  {sessionsLoading ? (
                    <div className="p-8 text-center">
                      <Loader2 className="h-8 w-8 mx-auto mb-3 text-gray-400 animate-spin" />
                      <p className="text-sm text-gray-500">Loading sessions...</p>
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Monitor className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No active sessions found</p>
                      <p className="text-xs mt-1">
                        This may indicate an issue with session tracking
                      </p>
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <div
                        key={session.sessionId}
                        className={`p-4 flex items-center justify-between ${
                          session.isCurrent ? 'bg-green-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            session.isCurrent
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {session.deviceInfo?.includes('Mobile') ? (
                              <Smartphone className="h-5 w-5" />
                            ) : (
                              <Monitor className="h-5 w-5" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">
                                {session.deviceInfo || 'Unknown Device'}
                              </span>
                              {session.isCurrent && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                  Current Session
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                              {session.ipAddress && (
                                <span className="flex items-center gap-1">
                                  <Globe className="h-3 w-3" />
                                  {session.ipAddress}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Last active {formatRelativeTime(session.lastActivityAt)}
                              </span>
                            </div>
                            <div className="mt-0.5 text-xs text-gray-400">
                              Started {new Date(session.createdAt).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeSession(session.sessionId)}
                          disabled={revokingSession === session.sessionId}
                          className={`text-sm px-3 py-1.5 rounded-lg ${
                            session.isCurrent
                              ? 'text-red-600 hover:bg-red-100'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {revokingSession === session.sessionId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : session.isCurrent ? (
                            'Log Out'
                          ) : (
                            'Revoke'
                          )}
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {sessions.length > 0 && (
                  <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
                    </span>
                    <button
                      onClick={fetchSessions}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Logs Tab (Admin Only) */}
          {activeTab === 'audit' && user?.role === 'admin' && (
            <div className="space-y-6">
              {/* Header with search and export */}
              <div className="card">
                <div className="p-6 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Audit Logs
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Track all actions and changes across the platform
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowAuditFilters(!showAuditFilters)}
                      className={`btn-secondary flex items-center gap-2 ${
                        hasActiveFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : ''
                      }`}
                    >
                      <Filter className="h-4 w-4" />
                      Filters
                      {hasActiveFilters && (
                        <span className="px-1.5 py-0.5 text-xs bg-primary-100 rounded-full">
                          {Object.values(auditFilters).filter(v => v !== '').length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={handleExportAuditLogs}
                      disabled={auditExporting}
                      className="btn-secondary flex items-center gap-2"
                    >
                      {auditExporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* Filters panel */}
                {showAuditFilters && (
                  <div className="p-4 bg-gray-50 border-b">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Search */}
                      <div className="lg:col-span-3">
                        <label className="label">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={auditFilters.search}
                            onChange={(e) => setAuditFilters({ ...auditFilters, search: e.target.value })}
                            className="input pl-10"
                            placeholder="Search actions, resources, or details..."
                          />
                        </div>
                      </div>

                      {/* Resource Type */}
                      <div>
                        <label className="label">Resource Type</label>
                        <select
                          value={auditFilters.resource_type}
                          onChange={(e) => setAuditFilters({ ...auditFilters, resource_type: e.target.value })}
                          className="input"
                        >
                          <option value="">All Types</option>
                          <option value="user">Users</option>
                          <option value="control">Controls</option>
                          <option value="evidence">Evidence</option>
                          <option value="document">Documents</option>
                          <option value="risk">Risks</option>
                          <option value="vendor">Vendors</option>
                          <option value="task">Tasks</option>
                          <option value="scan">Scans</option>
                          <option value="organization">Organization</option>
                          <option value="session">Sessions</option>
                          <option value="api_key">API Keys</option>
                        </select>
                      </div>

                      {/* User */}
                      <div>
                        <label className="label">User</label>
                        <select
                          value={auditFilters.user_id}
                          onChange={(e) => setAuditFilters({ ...auditFilters, user_id: e.target.value })}
                          className="input"
                        >
                          <option value="">All Users</option>
                          {auditUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.username || u.email}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Action */}
                      <div>
                        <label className="label">Action</label>
                        <input
                          type="text"
                          value={auditFilters.action}
                          onChange={(e) => setAuditFilters({ ...auditFilters, action: e.target.value })}
                          className="input"
                          placeholder="e.g., created, updated, deleted"
                        />
                      </div>

                      {/* Date Range */}
                      <div>
                        <label className="label">Start Date</label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="date"
                            value={auditFilters.start_date}
                            onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value })}
                            className="input pl-10"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="label">End Date</label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="date"
                            value={auditFilters.end_date}
                            onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value })}
                            className="input pl-10"
                          />
                        </div>
                      </div>

                      {/* Clear filters */}
                      {hasActiveFilters && (
                        <div className="flex items-end">
                          <button
                            onClick={clearAuditFilters}
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            Clear all filters
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Audit Log Table */}
                <div className="overflow-x-auto">
                  {auditLoading ? (
                    <div className="p-12 text-center">
                      <Loader2 className="h-8 w-8 mx-auto mb-3 text-gray-400 animate-spin" />
                      <p className="text-sm text-gray-500">Loading audit logs...</p>
                    </div>
                  ) : auditLogs.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No audit logs found</p>
                      {hasActiveFilters && (
                        <p className="text-xs mt-1">
                          Try adjusting your filters or <button onClick={clearAuditFilters} className="text-primary-600 hover:underline">clear all filters</button>
                        </p>
                      )}
                    </div>
                  ) : (
                    <table className="w-full min-w-[800px]">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Resource
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {new Date(log.timestamp).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {log.user_display_name || log.username || 'System'}
                              </div>
                              {log.ip_address && (
                                <div className="text-xs text-gray-500">
                                  {log.ip_address}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                log.action.includes('created') ? 'bg-green-100 text-green-800' :
                                log.action.includes('updated') || log.action.includes('modified') ? 'bg-blue-100 text-blue-800' :
                                log.action.includes('deleted') || log.action.includes('revoked') ? 'bg-red-100 text-red-800' :
                                log.action.includes('login') || log.action.includes('authenticated') ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {log.action.replace(/[._]/g, ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 capitalize">
                                {log.resource_type.replace(/_/g, ' ')}
                              </div>
                              <div className="text-xs text-gray-500 font-mono truncate max-w-[150px]" title={log.resource_id}>
                                {log.resource_id.length > 20
                                  ? `${log.resource_id.slice(0, 8)}...${log.resource_id.slice(-8)}`
                                  : log.resource_id}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-600 max-w-[200px] truncate" title={log.details || ''}>
                                {log.details || '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {log.status === 'failed' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Failed
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Success
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                {auditLogs.length > 0 && (
                  <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Showing {auditPagination.offset + 1} - {Math.min(auditPagination.offset + auditLogs.length, auditPagination.total)} of {auditPagination.total} entries
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAuditPageChange(Math.max(0, auditPagination.offset - auditPagination.limit))}
                        disabled={auditPagination.offset === 0}
                        className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-gray-600 px-2">
                        Page {Math.floor(auditPagination.offset / auditPagination.limit) + 1} of {Math.ceil(auditPagination.total / auditPagination.limit)}
                      </span>
                      <button
                        onClick={() => handleAuditPageChange(auditPagination.offset + auditPagination.limit)}
                        disabled={!auditPagination.hasMore}
                        className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dark Web Settings Tab (Admin Only) - Issue #303 */}
          {activeTab === 'dark-web' && user?.role === 'admin' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="card">
                <div className="p-6 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Skull className="h-5 w-5" />
                        Dark Web Monitoring
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Configure automated dark web scans and alert notifications
                      </p>
                    </div>
                    <button
                      onClick={fetchDarkWebSettings}
                      disabled={darkWebLoading}
                      className="btn-secondary flex items-center gap-2"
                    >
                      {darkWebLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Refresh
                    </button>
                  </div>
                </div>

                {darkWebLoading ? (
                  <div className="p-12 text-center">
                    <Loader2 className="h-8 w-8 mx-auto mb-3 text-gray-400 animate-spin" />
                    <p className="text-sm text-gray-500">Loading settings...</p>
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {/* Daily Scans Toggle */}
                    <div className="flex items-start gap-4">
                      <div className="flex items-center h-6">
                        <input
                          id="enableDailyScans"
                          type="checkbox"
                          checked={darkWebSettings.enableDailyScans}
                          onChange={(e) => setDarkWebSettings(prev => ({ ...prev, enableDailyScans: e.target.checked }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor="enableDailyScans" className="text-sm font-medium text-gray-900 cursor-pointer">
                          Enable daily automated scans
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Automatically scan your organization's domains for dark web exposures once per day.
                          Scans check Have I Been Pwned and Hudson Rock databases.
                        </p>
                      </div>
                    </div>

                    {/* Last Scan Info */}
                    {darkWebSettings.lastScanAt && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="h-4 w-4" />
                          <span>Last automated scan: {formatDateTime(darkWebSettings.lastScanAt)}</span>
                        </div>
                      </div>
                    )}

                    <div className="border-t pt-6">
                      <h3 className="text-sm font-medium text-gray-900 mb-4">Alert Notifications</h3>
                      <p className="text-xs text-gray-500 mb-4">
                        Choose how you want to be notified when new dark web exposures are detected.
                      </p>

                      {/* Email Alerts Toggle */}
                      <div className="flex items-start gap-4 mb-4">
                        <div className="flex items-center h-6">
                          <input
                            id="emailAlertsEnabled"
                            type="checkbox"
                            checked={darkWebSettings.emailAlertsEnabled}
                            onChange={(e) => setDarkWebSettings(prev => ({ ...prev, emailAlertsEnabled: e.target.checked }))}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label htmlFor="emailAlertsEnabled" className="text-sm font-medium text-gray-900 cursor-pointer">
                            Email alerts
                          </label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Send email notifications to all admin users when exposures are found.
                          </p>
                        </div>
                      </div>

                      {/* Extra Email Recipients */}
                      {darkWebSettings.emailAlertsEnabled && (
                        <div className="ml-8 mb-4">
                          <label htmlFor="emailRecipients" className="block text-sm font-medium text-gray-700 mb-1">
                            Additional email recipients (optional)
                          </label>
                          <input
                            id="emailRecipients"
                            type="text"
                            value={darkWebSettings.emailRecipients.join(', ')}
                            onChange={(e) => {
                              const emails = e.target.value
                                .split(',')
                                .map(email => email.trim())
                                .filter(email => email.length > 0)
                              setDarkWebSettings(prev => ({ ...prev, emailRecipients: emails }))
                            }}
                            placeholder="e.g., security@company.com, ciso@company.com"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Comma-separated list of additional email addresses. All tenant admins will also receive alerts.
                          </p>
                        </div>
                      )}

                      {/* Webhook Alerts Toggle */}
                      <div className="flex items-start gap-4">
                        <div className="flex items-center h-6">
                          <input
                            id="webhookAlertsEnabled"
                            type="checkbox"
                            checked={darkWebSettings.webhookAlertsEnabled}
                            onChange={(e) => setDarkWebSettings(prev => ({ ...prev, webhookAlertsEnabled: e.target.checked }))}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label htmlFor="webhookAlertsEnabled" className="text-sm font-medium text-gray-900 cursor-pointer">
                            Webhook alerts
                          </label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Trigger webhooks configured to receive dark_web_exposure_detected events.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-700">
                          <p className="font-medium">About Dark Web Scanning</p>
                          <ul className="mt-1 list-disc list-inside space-y-0.5 text-blue-600">
                            <li>Scans run daily against all organization domains</li>
                            <li>Uses Have I Been Pwned and Hudson Rock APIs</li>
                            <li>Duplicate alerts are automatically filtered</li>
                            <li>Configure notification channels in the Notifications tab</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="p-4 bg-gray-50 border-t flex justify-end">
                  <button
                    onClick={handleSaveDarkWebSettings}
                    disabled={darkWebSaving || darkWebLoading}
                    className="btn-primary flex items-center gap-2"
                  >
                    {darkWebSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Data Export Tab (Admin Only) - Issue #91 */}
          {activeTab === 'export' && user?.role === 'admin' && (
            <div className="space-y-6">
              {/* Header */}
              <div className="card">
                <div className="p-6 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <HardDrive className="h-5 w-5" />
                        Data Export & Backup
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Export your compliance data for backup, reporting, or migration purposes
                      </p>
                    </div>
                    <button
                      onClick={fetchExportSummary}
                      disabled={exportLoading}
                      className="btn-secondary flex items-center gap-2"
                    >
                      {exportLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Export Options */}
                <div className="p-6 space-y-6">
                  {/* Format Selection */}
                  <div>
                    <label className="label mb-2">Export Format</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="exportFormat"
                          value="csv"
                          checked={exportFormat === 'csv'}
                          onChange={() => setExportFormat('csv')}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <FileSpreadsheet className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">CSV (Spreadsheet)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="exportFormat"
                          value="json"
                          checked={exportFormat === 'json'}
                          onChange={() => setExportFormat('json')}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <FileJson className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">JSON (Structured Data)</span>
                      </label>
                    </div>
                  </div>

                  {/* Include Related Data Option */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={exportIncludeRelated}
                        onChange={() => setExportIncludeRelated(!exportIncludeRelated)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Include related data (evidence with controls, assessments with vendors, etc.)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Data Types Selection */}
              <div className="card">
                <div className="p-6 border-b flex items-center justify-between">
                  <h3 className="text-base font-medium text-gray-900">Select Data to Export</h3>
                  <button
                    onClick={toggleAllExportSelections}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {exportLoading ? (
                  <div className="p-12 text-center">
                    <Loader2 className="h-8 w-8 mx-auto mb-3 text-gray-400 animate-spin" />
                    <p className="text-sm text-gray-500">Loading export summary...</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {/* Controls */}
                    <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={exportSelections.controls}
                          onChange={() => toggleExportSelection('controls')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <Shield className="h-5 w-5 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Controls</p>
                          <p className="text-xs text-gray-500">{exportSummary?.controls || 0} records</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExport('controls')}
                        disabled={exportInProgress !== null}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                      >
                        {exportInProgress === 'controls' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>

                    {/* Risks */}
                    <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={exportSelections.risks}
                          onChange={() => toggleExportSelection('risks')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Risks</p>
                          <p className="text-xs text-gray-500">{exportSummary?.risks || 0} records</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExport('risks')}
                        disabled={exportInProgress !== null}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                      >
                        {exportInProgress === 'risks' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>

                    {/* Evidence */}
                    <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={exportSelections.evidence}
                          onChange={() => toggleExportSelection('evidence')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <Archive className="h-5 w-5 text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Evidence</p>
                          <p className="text-xs text-gray-500">{exportSummary?.evidence || 0} records (metadata only)</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExport('evidence')}
                        disabled={exportInProgress !== null}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                      >
                        {exportInProgress === 'evidence' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>

                    {/* Documents */}
                    <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={exportSelections.documents}
                          onChange={() => toggleExportSelection('documents')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <FileText className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Documents (Policies)</p>
                          <p className="text-xs text-gray-500">{exportSummary?.documents || 0} records</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExport('documents')}
                        disabled={exportInProgress !== null}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                      >
                        {exportInProgress === 'documents' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>

                    {/* Vendors */}
                    <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={exportSelections.vendors}
                          onChange={() => toggleExportSelection('vendors')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <Building2 className="h-5 w-5 text-orange-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Vendors (TPRM)</p>
                          <p className="text-xs text-gray-500">{exportSummary?.vendors || 0} records</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExport('vendors')}
                        disabled={exportInProgress !== null}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                      >
                        {exportInProgress === 'vendors' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>

                    {/* Tasks */}
                    <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={exportSelections.tasks}
                          onChange={() => toggleExportSelection('tasks')}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <CheckSquare className="h-5 w-5 text-cyan-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Tasks</p>
                          <p className="text-xs text-gray-500">{exportSummary?.tasks || 0} records</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExport('tasks')}
                        disabled={exportInProgress !== null}
                        className="btn-secondary text-sm py-1.5 px-3 flex items-center gap-1"
                      >
                        {exportInProgress === 'tasks' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </button>
                    </div>
                  </div>
                )}

                {/* Batch Export Button */}
                <div className="p-4 bg-gray-50 border-t">
                  <button
                    onClick={handleExportSelected}
                    disabled={exportInProgress !== null || noneSelected}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {exportInProgress !== null && exportInProgress !== 'all' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Export Selected ({Object.values(exportSelections).filter(Boolean).length} types)
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Full Backup Section */}
              <div className="card">
                <div className="p-6 border-b">
                  <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary-500" />
                    Full Backup
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Export all data as a single JSON file for complete backup or migration
                  </p>
                </div>
                <div className="p-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Package className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">Full backup includes:</p>
                        <ul className="mt-1 list-disc list-inside space-y-0.5 text-blue-600">
                          <li>All controls with linked evidence and policies</li>
                          <li>Complete risk register with assessments</li>
                          <li>All evidence records (metadata only, not files)</li>
                          <li>Document/policy metadata and versions</li>
                          <li>Vendor records with risk assessments</li>
                          <li>All tasks and their assignments</li>
                          <li>Framework configurations</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleFullBackup}
                    disabled={exportInProgress !== null}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {exportInProgress === 'all' ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Preparing Backup...
                      </>
                    ) : (
                      <>
                        <Package className="h-4 w-4" />
                        Download Full Backup (JSON)
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Info Section */}
              <div className="card p-6 bg-gray-50">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-600">
                    <p className="font-medium text-gray-700">Export Notes:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>CSV exports are best for spreadsheet analysis and reporting</li>
                      <li>JSON exports preserve all data relationships and are best for backups</li>
                      <li>Evidence files are not included in exports (metadata only)</li>
                      <li>Exports may take a few moments for large datasets</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* 2FA Setup Modal */}
      {showSetup2FAModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={close2FAModals} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {setup2FAStep === 'qr' && 'Set Up Two-Factor Authentication'}
                    {setup2FAStep === 'verify' && 'Verify Your Code'}
                    {setup2FAStep === 'backup' && 'Save Your Backup Codes'}
                  </h3>
                  <button onClick={close2FAModals} className="text-gray-400 hover:text-gray-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {setup2FAStep === 'qr' && setup2FAData && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    <div className="flex justify-center py-4">
                      <img src={setup2FAData.qrCodeDataUrl} alt="2FA QR Code" className="border rounded-lg" />
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Or enter this code manually:</p>
                      <code className="text-sm font-mono text-gray-900 break-all">{setup2FAData.secret}</code>
                    </div>
                    <button
                      onClick={() => setSetup2FAStep('verify')}
                      className="btn-primary w-full"
                    >
                      Continue
                    </button>
                  </div>
                )}

                {setup2FAStep === 'verify' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Enter the 6-digit code from your authenticator app to verify setup.
                    </p>
                    <div>
                      <label className="label">Verification Code</label>
                      <input
                        type="text"
                        value={twoFACode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                          setTwoFACode(val)
                          setTwoFAError('')
                        }}
                        className="input text-center text-2xl tracking-widest font-mono"
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                      />
                      {twoFAError && (
                        <p className="text-sm text-red-600 mt-1">{twoFAError}</p>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setSetup2FAStep('qr')}
                        className="btn-secondary flex-1"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleVerify2FASetup}
                        disabled={twoFALoading || twoFACode.length !== 6}
                        className="btn-primary flex-1"
                      >
                        {twoFALoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                      </button>
                    </div>
                  </div>
                )}

                {setup2FAStep === 'backup' && backupCodes.length > 0 && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-700">
                          Save these backup codes in a secure location. You can use them to access your account if you lose your authenticator device.
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-2">
                        {backupCodes.map((code, i) => (
                          <code key={i} className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                            {code}
                          </code>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={copyBackupCodesToClipboard}
                      className="btn-secondary w-full"
                    >
                      {copiedBackupCodes ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Backup Codes
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        close2FAModals()
                        showMessage('success', '2FA has been enabled successfully!')
                      }}
                      className="btn-primary w-full"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisable2FAModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={close2FAModals} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Disable Two-Factor Authentication</h3>
                  <button onClick={close2FAModals} className="text-gray-400 hover:text-gray-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">
                        Disabling 2FA will make your account less secure. You will only need your email verification to log in.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="label">Enter a code from your authenticator app or a backup code</label>
                    <input
                      type="text"
                      value={twoFACode}
                      onChange={(e) => {
                        setTwoFACode(e.target.value)
                        setTwoFAError('')
                      }}
                      className="input"
                      placeholder="Enter code"
                      autoFocus
                    />
                    {twoFAError && (
                      <p className="text-sm text-red-600 mt-1">{twoFAError}</p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button onClick={close2FAModals} className="btn-secondary flex-1">
                      Cancel
                    </button>
                    <button
                      onClick={handleDisable2FA}
                      disabled={twoFALoading || !twoFACode}
                      className="btn-danger flex-1"
                    >
                      {twoFALoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable 2FA'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showBackupCodesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={close2FAModals} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {backupCodes.length > 0 ? 'Your New Backup Codes' : 'Generate New Backup Codes'}
                  </h3>
                  <button onClick={close2FAModals} className="text-gray-400 hover:text-gray-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {backupCodes.length === 0 ? (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-700">
                          Generating new backup codes will invalidate all your existing backup codes.
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="label">Enter a code from your authenticator app</label>
                      <input
                        type="text"
                        value={twoFACode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                          setTwoFACode(val)
                          setTwoFAError('')
                        }}
                        className="input text-center text-2xl tracking-widest font-mono"
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                      />
                      {twoFAError && (
                        <p className="text-sm text-red-600 mt-1">{twoFAError}</p>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <button onClick={close2FAModals} className="btn-secondary flex-1">
                        Cancel
                      </button>
                      <button
                        onClick={handleRegenerateBackupCodes}
                        disabled={twoFALoading || twoFACode.length !== 6}
                        className="btn-primary flex-1"
                      >
                        {twoFALoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-700">
                          Save these backup codes in a secure location. Your old codes are no longer valid.
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-2">
                        {backupCodes.map((code, i) => (
                          <code key={i} className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border">
                            {code}
                          </code>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={copyBackupCodesToClipboard}
                      className="btn-secondary w-full"
                    >
                      {copiedBackupCodes ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Backup Codes
                        </>
                      )}
                    </button>
                    <button onClick={close2FAModals} className="btn-primary w-full">
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Change Modal (Issue #106) */}
      {showEmailChangeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeEmailChangeModal} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary-600" />
                    {emailChangeStep === 'enter' && 'Change Email Address'}
                    {emailChangeStep === 'verify' && 'Verify New Email'}
                    {emailChangeStep === 'success' && 'Email Changed'}
                  </h3>
                  <button onClick={closeEmailChangeModal} className="text-gray-400 hover:text-gray-500">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {emailChangeStep === 'enter' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Enter your new email address. A verification code will be sent to confirm the change.
                    </p>
                    <div>
                      <label className="label">Current Email</label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        className="input bg-gray-50 text-gray-500"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="label">New Email Address</label>
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => {
                          setNewEmail(e.target.value)
                          setEmailChangeError('')
                        }}
                        className="input"
                        placeholder="newemail@example.com"
                        autoFocus
                      />
                      {emailChangeError && (
                        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {emailChangeError}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={closeEmailChangeModal}
                        className="btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRequestEmailChange}
                        disabled={emailChangeLoading || !newEmail.trim()}
                        className="btn-primary flex-1"
                      >
                        {emailChangeLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Send Code'
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {emailChangeStep === 'verify' && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      A 6-digit verification code has been sent to <strong>{newEmail}</strong>. Enter it below to confirm your new email address.
                    </p>
                    <div>
                      <label className="label">Verification Code</label>
                      <input
                        type="text"
                        value={emailChangeCode}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                          setEmailChangeCode(val)
                          setEmailChangeError('')
                        }}
                        className="input text-center text-2xl tracking-widest font-mono"
                        placeholder="000000"
                        maxLength={6}
                        autoFocus
                      />
                      {emailChangeError && (
                        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {emailChangeError}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      The code expires in 15 minutes. Check your spam folder if you don't see it.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setEmailChangeStep('enter')
                          setEmailChangeCode('')
                          setEmailChangeError('')
                        }}
                        className="btn-secondary flex-1"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleVerifyEmailChange}
                        disabled={emailChangeLoading || emailChangeCode.length !== 6}
                        className="btn-primary flex-1"
                      >
                        {emailChangeLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Verify'
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {emailChangeStep === 'success' && (
                  <div className="space-y-4 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-gray-900">Email Updated</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Your email has been changed to <strong>{newEmail}</strong>.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        A notification has been sent to your old email address.
                      </p>
                    </div>
                    <button
                      onClick={closeEmailChangeModal}
                      className="btn-primary w-full"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
